import { TrashIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import { ErrorMessage } from '@hookform/error-message';
import React, { useEffect, useState } from 'react';
import { useFieldArray, Controller, useFormContext } from 'react-hook-form';

import BaseInputProps from './BaseInputProps';

import ErrorMessageText from './ErrorMessageText';
import InputLabel from './InputLabel';

export interface DynamicTextInputProps extends BaseInputProps {
  validation: BaseInputProps["validation"] &  {
    email?: boolean;
    wordCount?: number;
  };
}

export default function DynamicTextInput({ name, label, validation }: DynamicTextInputProps) {
  const { control, formState, setError, clearErrors, getValues } = useFormContext();
  const [isFocused, setIsFocused] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: name,
    keyName: "key",
  });

  useEffect(() => {
    if (fields.length === 0) {
      append('');
    }
  }, [fields, append]);

  useEffect(() => {
    if (formState.submitCount === 0) return; // necessary to 

    const requiresMinLength = validation.minLength && formState.submitCount > 0;
    const requiresMaxLength = validation.maxLength && formState.submitCount > 0;
    if ((requiresMinLength && fields.length < validation.minLength) || (requiresMaxLength && fields.length > validation.maxLength)) {
      if (fields.length < validation.minLength) {
        setError(name, {
          type: "manual",
          message: `At least ${validation.minLength} items are required.`,
        });
      }

      if (fields.length > validation.maxLength) {
        setError(name, {
          type: "manual",
          message: `Maximum of ${validation.maxLength} items are allowed.`,
        });
      }
    } else {
      clearErrors(name);
    }

    fields.forEach((fieldItem, index) => {
      const fieldValue = getValues(`${name}[${index}]`);
      const wordCount = fieldValue.trim().split(/\s+/).length;

      if (typeof fieldValue === 'string') {
        if (validation.email) {
          const emailRegex = /^\S+@\S+\.\S+$/;

          if (formState.submitCount > 0 && !fieldValue.match(emailRegex)) {
            setError(`${name}[${index}]`, {
              type: "manual",
              message: "Invalid email address.",
            });
          } else {
            clearErrors(`${name}[${index}]`);
          }
        } else if (formState.submitCount > 0 && (validation.minLength > 0 || fields.length > 1) && wordCount < (validation.wordCount || 3)) {
          setError(`${name}[${index}]`, {
            type: "manual",
            message: "Must have at least three words.",
          });
        } else {
          clearErrors(`${name}[${index}]`);
        }
      }
    });
  }, [formState.submitCount]);

  const conditionsExplainer: string[] = [];

  if (validation.minLength) {
    if (validation.minLength === 0) {
      conditionsExplainer.push("Optional");
    }
    if (validation.minLength > 0) {
      conditionsExplainer.push(`Min ${validation.minLength}`);
    }
  }

  if (validation.maxLength) {
    conditionsExplainer.push(`Max ${validation.maxLength}`);
  }

  let labelAssist = "";
  if (conditionsExplainer.length > 0) {
    labelAssist = ` (${conditionsExplainer.join(", ")})`;
  }

  return (
    <div className="flex flex-col mb-4 w-full px-2">
      <InputLabel label={`${label}${labelAssist}`} name={name} isFocused={isFocused} />
      {fields.map((item, index) => (
        <React.Fragment key={item.id}>
          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name={`${name}[${index}]`}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="mt-2 text-base p-2 block w-full rounded-md border-0 text-limed-spruce-800 shadow-sm ring-1 ring-inset ring-porcelain-300 placeholder:text-porcelain-400 focus:ring-2 focus:ring-inset focus:ring-bondi-blue-600"
                />
              )}
            />
            {
              <button
                type="button"
                onClick={() => remove(index)}
                className="mt-2 px-2 py-2 border border-[#949494] text-[#949494] rounded-full whitespace-nowrap">
                <TrashIcon className="h-4 w-4" />
              </button>
            }
          </div>
          <ErrorMessage errors={formState.errors} name={`${name}[${index}]`} render={({ message }) => <ErrorMessageText message={message} />} />
        </React.Fragment>
      ))}
      <ErrorMessage errors={formState.errors} name={name} render={({ message }) => <ErrorMessageText message={message} />} />
      {fields.length < (validation?.maxLength || 10) && (
        <div className="grid justify-items-end">
          <button
            type="button"
            onClick={() => append('')}
            className="mt-[11px] px-2 py-2 border border-bondi-blue-600 text-bondi-blue-800 rounded-full inline-flex items-center">
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
